import { useState, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight, ChevronLeft, Sparkles } from "lucide-react";

const PAD = 8;       // padding around highlighted element
const TIP_MARGIN = 14;
const TIP_W = 320;

/**
 * Interactive product tour. Receives a list of `steps`, each step pointing
 * to a `selector` (CSS or data-testid) plus copy. Highlights the target with
 * a cutout overlay and shows a tooltip that auto-flips on screen edges.
 *
 * Skipping or completing the tour calls `onFinish(skipped: boolean)`.
 *
 * Steps format:
 *   { selector: "[data-testid=fr-sidebar]", title: "…", body: "…", placement?: "auto|top|bottom" }
 */
export default function OnboardingTour({ steps, onFinish, brandColor = "#FF6B00" }) {
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [tipPos, setTipPos] = useState(null);
  const tipRef = useRef(null);

  const step = steps[idx];

  const measure = useCallback(() => {
    if (!step) return;
    const el = document.querySelector(step.selector);
    if (!el) {
      // No target — center the tip with a phantom rect at viewport center
      const w = window.innerWidth, h = window.innerHeight;
      setRect({ top: h / 2 - 1, left: w / 2 - 1, width: 2, height: 2, ghost: true });
    } else {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height, ghost: false });
      // Try to scroll target into view so step always visible
      try {
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
      } catch { /* ignore */ }
    }
  }, [step]);

  useLayoutEffect(() => {
    measure();
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    const iv = setInterval(measure, 600); // keep tracking moving elements
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
      clearInterval(iv);
    };
  }, [measure]);

  // Recompute tooltip position whenever target rect changes
  useEffect(() => {
    if (!rect) return;
    const w = window.innerWidth, h = window.innerHeight;
    const tipH = tipRef.current?.offsetHeight || 200;
    let placement = step.placement || "auto";
    if (placement === "auto") {
      const spaceBelow = h - (rect.top + rect.height);
      const spaceAbove = rect.top;
      placement = spaceBelow > tipH + 40 || spaceBelow > spaceAbove ? "bottom" : "top";
    }
    let top, left;
    if (placement === "bottom") {
      top = rect.top + rect.height + TIP_MARGIN;
    } else {
      top = rect.top - tipH - TIP_MARGIN;
    }
    left = rect.left + rect.width / 2 - TIP_W / 2;
    // clamp
    left = Math.max(12, Math.min(w - TIP_W - 12, left));
    top = Math.max(12, Math.min(h - tipH - 12, top));
    setTipPos({ top, left, placement });
  }, [rect, step]);

  const next = () => {
    if (idx + 1 >= steps.length) {
      onFinish?.(false);
    } else {
      setIdx(idx + 1);
    }
  };
  const back = () => idx > 0 && setIdx(idx - 1);
  const skip = () => onFinish?.(true);

  if (!step || !rect) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="tour-overlay"
        className="fixed inset-0 z-[200]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        data-testid="onboarding-overlay"
      >
        {/* SVG mask: dim background with a cutout around the target */}
        <svg className="absolute inset-0 w-full h-full pointer-events-auto" onClick={skip}>
          <defs>
            <mask id="tour-cutout">
              <rect width="100%" height="100%" fill="white" />
              {!rect.ghost && (
                <rect
                  x={Math.max(0, rect.left - PAD)}
                  y={Math.max(0, rect.top - PAD)}
                  width={rect.width + PAD * 2}
                  height={rect.height + PAD * 2}
                  rx={14}
                  ry={14}
                  fill="black"
                />
              )}
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="rgba(8,9,12,0.72)" mask="url(#tour-cutout)" />
        </svg>

        {/* Glow ring around target */}
        {!rect.ghost && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="pointer-events-none absolute rounded-2xl"
            style={{
              top: rect.top - PAD,
              left: rect.left - PAD,
              width: rect.width + PAD * 2,
              height: rect.height + PAD * 2,
              boxShadow: `0 0 0 2px ${brandColor}, 0 0 0 6px ${brandColor}33, 0 0 32px ${brandColor}66`,
            }}
          />
        )}

        {/* Tooltip card */}
        {tipPos && (
          <motion.div
            ref={tipRef}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: "spring", stiffness: 360, damping: 26 }}
            className="absolute bg-white rounded-2xl shadow-2xl border border-black/5 overflow-hidden"
            style={{ top: tipPos.top, left: tipPos.left, width: TIP_W }}
            data-testid="onboarding-tooltip"
          >
            <div className="px-4 pt-4 pb-3 flex items-start gap-3">
              <div
                className="w-9 h-9 rounded-xl shrink-0 flex items-center justify-center text-white shadow-md"
                style={{ background: `linear-gradient(135deg, ${brandColor}, #F43F5E)` }}
              >
                <Sparkles className="w-5 h-5" strokeWidth={2.4} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] uppercase tracking-widest font-extrabold text-gray-400">
                    Passo {idx + 1} de {steps.length}
                  </span>
                  <button
                    onClick={skip}
                    className="w-7 h-7 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400"
                    data-testid="onboarding-close"
                    aria-label="Pular tour"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <h3 className="text-[15px] font-extrabold text-gray-900 leading-tight mt-0.5" data-testid="onboarding-title">
                  {step.title}
                </h3>
              </div>
            </div>
            <p className="px-4 pb-4 text-sm text-gray-600 leading-snug" data-testid="onboarding-body">
              {step.body}
            </p>
            <div className="flex items-center justify-between px-3 py-2.5 bg-gray-50 border-t border-gray-100">
              <button
                onClick={skip}
                className="text-xs font-bold text-gray-500 hover:text-gray-700 px-2 py-1.5"
                data-testid="onboarding-skip"
              >
                Pular tour
              </button>
              <div className="flex items-center gap-1.5">
                {idx > 0 && (
                  <button
                    onClick={back}
                    className="px-3 py-1.5 rounded-lg hover:bg-gray-200 text-gray-700 text-sm font-bold flex items-center gap-1"
                    data-testid="onboarding-back"
                  >
                    <ChevronLeft className="w-4 h-4" /> Voltar
                  </button>
                )}
                <button
                  onClick={next}
                  className="px-3.5 py-1.5 rounded-lg text-white text-sm font-extrabold flex items-center gap-1 shadow-md active:scale-95 transition-transform"
                  style={{ background: brandColor }}
                  data-testid="onboarding-next"
                >
                  {idx + 1 >= steps.length ? "Concluir" : "Próximo"}
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
            {/* Progress dots */}
            <div className="px-4 pb-3 flex items-center justify-center gap-1.5">
              {steps.map((_, i) => (
                <div
                  key={i}
                  className="h-1.5 rounded-full transition-all"
                  style={{
                    width: i === idx ? 18 : 6,
                    background: i <= idx ? brandColor : "#E5E7EB",
                  }}
                />
              ))}
            </div>
          </motion.div>
        )}
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}
